/**
 * Chart configuration constants for DFP plugins
 * Provides consistent chart styling and configuration across all components
 */

/**
 * Default chart element configurations
 */
export const CHART_DEFAULTS = {
  dot: { r: 4 },
  activeDot: { r: 6 },
  strokeWidth: 3,
} as const;

/**
 * Chart container background styles
 */
export const CHART_CONTAINER_STYLES = {
  background: {
    dark: 'linear-gradient(135deg, #424242 0%, #303030 100%)',
    light: 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
  },
  borderRadius: '6px',
} as const;

/**
 * Tooltip configuration
 */
export const CHART_TOOLTIP_CONFIG = {
  contentStyle: (isDark: boolean) => ({
    backgroundColor: isDark ? 'rgba(48, 48, 48, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    borderRadius: '6px',
    padding: '10px',
    color: isDark ? '#fff' : '#000',
    zIndex: 9999,
    pointerEvents: 'none' as const,
  }),
  itemStyle: (isDark: boolean) => ({
    color: isDark ? '#fff' : '#000',
    fontSize: '0.875rem',
  }),
  labelStyle: (isDark: boolean) => ({
    color: isDark ? '#fff' : '#000',
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: '4px',
  }),
} as const;

/**
 * Get chart container background based on theme
 * @param isDark - Whether dark theme is active
 * @returns Background gradient string
 */
export const getChartContainerBackground = (isDark: boolean): string => {
  return isDark 
    ? CHART_CONTAINER_STYLES.background.dark 
    : CHART_CONTAINER_STYLES.background.light;
};

/**
 * Get tooltip content style based on theme
 * @param isDark - Whether dark theme is active
 * @returns Tooltip content style object
 */
export const getTooltipContentStyle = (isDark: boolean) => {
  return CHART_TOOLTIP_CONFIG.contentStyle(isDark);
};

/**
 * Get tooltip item style based on theme
 * @param isDark - Whether dark theme is active
 * @returns Tooltip item style object
 */
export const getTooltipItemStyle = (isDark: boolean) => {
  return CHART_TOOLTIP_CONFIG.itemStyle(isDark);
};

/**
 * Get tooltip label style based on theme
 * @param isDark - Whether dark theme is active
 * @returns Tooltip label style object
 */
export const getTooltipLabelStyle = (isDark: boolean) => {
  return CHART_TOOLTIP_CONFIG.labelStyle(isDark);
};
